// Pure mapping from a Closed-Won modal entry to the engine-keyed client
// revenue fields, so a saved won deal reads back through
// mapClientToEngagements (REVENUE_ENGINE_SPEC.md) as exactly the intended
// income line(s). Returns ONLY the revenue-relevant fields; the caller merges
// them onto the base client (name, household, dates, etc.).
//
// Keyed on the P&L income category the modal now offers per division. The
// point is that the SAVED field values (clientType / serviceTier / fees) are
// what determine the income line, not a heuristic guess at display time. That
// makes the mapping testable in isolation: build the derived client, run it
// through mapClientToEngagements, and assert the category.
//
// Rules:
//   subscription_fees (BWM): monthly recurring subscription; if an asset
//     amount is present, ALSO an AUM engagement (fee = assets * 0.5%). A
//     monthly of 0 with an annual amount is an annual-pay subscription.
//   investment_management (BWM): AUM only (fee = assets * 0.5%, or the annual
//     fee entered directly when no asset amount is given).
//   tax_planning (BWM): one-time committed tax plan (tier 'tax-plan' + annual).
//   bookkeeping / cao (BTS): monthly recurring, distinguished by clientType.
//     An annual amount with no monthly is spread evenly (annual / 12).
//   tax_preparation (BTS): one-time committed annual.
//   commissions (BIA): actuals-only; nothing is projected, annual kept for ref.

import { AUM_FEE_RATE } from './engine.mjs';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// assets * flat AUM rate (0.5%). Used when the user enters the asset amount
// (the firm's preferred input) rather than a dollar fee.
export function aumFeeFromAssets(aum) {
  return round2((Number(aum) || 0) * AUM_FEE_RATE);
}

// { category, division, monthly, annual, aum, serviceTier } -> engine fields.
// Every field is emitted explicitly so a stale value can't survive the merge
// onto the base client (e.g. an old monthly lingering on a tax-prep record).
export function deriveClientRevenueFields({ category, division, monthly = 0, annual = 0, aum = 0, serviceTier } = {}) {
  const m = Number(monthly) || 0;
  const a = Number(annual) || 0;
  const assets = Number(aum) || 0;
  const fee = aumFeeFromAssets(assets); // 0 when no assets entered

  const base = {
    clientType: '',
    serviceTier: serviceTier || '',
    monthlyRevenue: 0,
    annualRevenue: 0,
    investmentFeeAnnual: 0,
    taxPlanFee: 0,
    aum: assets,
  };

  switch (category) {
    case 'subscription_fees':
      // BWM Guided / Total Wealth. Monthly drives the subscription; a monthly
      // of 0 with an annual amount is an annual-pay subscription (engine
      // spreads it evenly). AUM assets add a second (AUM) engagement.
      if (m > 0) {
        return { ...base, monthlyRevenue: m, annualRevenue: round2(m * 12), investmentFeeAnnual: fee };
      }
      return { ...base, monthlyRevenue: 0, annualRevenue: a, investmentFeeAnnual: fee };

    case 'investment_management':
      // BWM Investment Only: AUM fee from assets, or the annual fee entered
      // directly when no asset amount is given. Tier set to investment-only so
      // the record reads as AUM-only (no phantom subscription engagement).
      return { ...base, serviceTier: 'investment-only', investmentFeeAnnual: fee > 0 ? fee : a };

    case 'tax_planning':
      // BWM Tax Plan: one-time committed. tier 'tax-plan' + annual is the
      // engine's tax-planning branch (matches how tax-plan clients are stored).
      return { ...base, serviceTier: 'tax-plan', annualRevenue: a };

    case 'bookkeeping':
      return { ...base, clientType: 'bookkeeping', monthlyRevenue: m > 0 ? m : round2(a / 12), annualRevenue: m > 0 ? round2(m * 12) : a };

    case 'cao':
      return { ...base, clientType: 'cao', monthlyRevenue: m > 0 ? m : round2(a / 12), annualRevenue: m > 0 ? round2(m * 12) : a };

    case 'tax_preparation':
      return { ...base, clientType: 'tax-prep', annualRevenue: a };

    case 'commissions':
      // BIA: actuals-only. Keep the annual as a reference; the engine projects
      // nothing for commissions (QuickBooks supplies the real numbers).
      return { ...base, annualRevenue: a };

    default:
      // Unknown category: division-sensible fallback so revenue is never
      // silently dropped.
      if (division === 'bia') return { ...base, annualRevenue: a };
      if (division === 'bts') {
        return m > 0
          ? { ...base, clientType: 'bookkeeping', monthlyRevenue: m, annualRevenue: round2(m * 12) }
          : { ...base, clientType: 'tax-prep', annualRevenue: a };
      }
      return { ...base, monthlyRevenue: m, annualRevenue: m > 0 ? round2(m * 12) : a, investmentFeeAnnual: fee };
  }
}

// The category options the modal offers per division. Ordered most-common
// first. Keeping this here (next to the derivation) keeps the two in lockstep.
export const CATEGORY_OPTIONS = {
  bwm: [
    { value: 'subscription_fees', label: 'Subscription Fees' },
    { value: 'investment_management', label: 'Investment Management' },
    { value: 'tax_planning', label: 'Tax Plan' },
  ],
  bts: [
    { value: 'bookkeeping', label: 'Bookkeeping' },
    { value: 'cao', label: 'Fractional CAO' },
    { value: 'tax_preparation', label: 'Tax Preparation' },
  ],
  bia: [
    { value: 'commissions', label: 'Commissions' },
  ],
};

// Sensible default category from the prospect's division + service tier.
// Replaces the old getDefaultCategory, whose tier checks referenced tier keys
// that don't exist in SERVICE_TIERS (so every BTS deal fell through to tax prep
// and CAO was unreachable).
export function defaultCategoryForTier(division, serviceTier) {
  if (division === 'bia') return 'commissions';
  if (division === 'bts') {
    // business-package is the recurring BTS tier -> default to bookkeeping
    // (user switches to Fractional CAO when that's the service). tax-individual
    // / tax-business are per-filing one-time.
    if (serviceTier === 'business-package') return 'bookkeeping';
    return 'tax_preparation';
  }
  // BWM
  if (serviceTier === 'tax-plan') return 'tax_planning';
  if (serviceTier === 'investment-only') return 'investment_management';
  return 'subscription_fees';
}
